import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPut } from '../../lib/api';

/**
 * THE RUN OF SHOW — designed at 390px and nowhere else.
 *
 * She is standing up, holding a phone in one hand, in a room that is filling with people. That is
 * the entire brief, and it decides everything:
 *
 * ROWS ARE 60px AND THE WHOLE ROW IS THE TARGET. Not a checkbox — a checkbox is an 18px target for
 * a thumb that is moving. The row is a button.
 *
 * IT WRITES OPTIMISTICALLY AND FORGIVES THE NETWORK. Venue wifi is bad and library wifi is worse.
 * The tick appears immediately, the request follows, and a failure puts it back with a line saying
 * so — rather than a spinner she watches while somebody asks her where the tables go.
 *
 * IT SENDS WHAT SHE MEANT, NOT A TOGGLE. A tap can arrive twice; a toggle that arrives twice leaves
 * the box in the state nobody chose.
 *
 * NOTHING BLOCKS AND NOTHING NAGS. No progress bar, no "you must complete setup first", no red.
 * The count at the top is there so she can see at a glance whether she has forgotten something,
 * and that is the whole of the pressure this screen applies.
 */

interface ChecklistItem {
  id: string;
  position: number;
  label: string;
  requiresPhoto: boolean;
  checkedAt: string | null;
  checkedBy: string | null;
  hasPhoto: boolean;
}

interface Checklist {
  id: string;
  sessionId: string;
  phase: 'setup' | 'run_of_show' | 'close_down';
  label: string;
  completedAt: string | null;
  items: ChecklistItem[];
}

const PHASE_LABEL: Record<Checklist['phase'], string> = {
  setup: 'Before doors open',
  run_of_show: 'During the session',
  close_down: 'Before you leave',
};

export function RunOfShow() {
  const { sessionId } = useParams();
  const queryClient = useQueryClient();
  const [failed, setFailed] = useState<string | null>(null);

  const run = useQuery({
    queryKey: ['run-of-show', sessionId],
    queryFn: () => apiFetch<{ lists: Checklist[] }>(`/api/sessions/${sessionId}/run-of-show`),
    enabled: !!sessionId,
  });

  const tick = useMutation({
    mutationFn: ({ itemId, checked }: { itemId: string; checked: boolean }) =>
      apiPut<{ lists: Checklist[] }>(`/api/run-of-show/items/${itemId}`, { checked }),

    /*
      Optimistic, and it puts the tick back if the request fails. On venue wifi the alternative is
      a screen that lags a second behind her thumb, which is how somebody taps the same row twice
      and then a third row by accident.
    */
    onMutate: async ({ itemId, checked }) => {
      await queryClient.cancelQueries({ queryKey: ['run-of-show', sessionId] });
      const previous = queryClient.getQueryData<{ lists: Checklist[] }>([
        'run-of-show',
        sessionId,
      ]);

      queryClient.setQueryData<{ lists: Checklist[] }>(['run-of-show', sessionId], (current) =>
        current
          ? {
              lists: current.lists.map((list) => ({
                ...list,
                items: list.items.map((item) =>
                  item.id === itemId
                    ? { ...item, checkedAt: checked ? new Date().toISOString() : null }
                    : item,
                ),
              })),
            }
          : current,
      );

      setFailed(null);
      return { previous };
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['run-of-show', sessionId], context.previous);
      }
      setFailed('That did not save. Check your signal and tap it again.');
    },

    onSuccess: (data) => {
      queryClient.setQueryData(['run-of-show', sessionId], data);
    },
  });

  if (run.isPending) {
    return <p className="px-5 py-10 text-sm text-ink-3">Loading…</p>;
  }

  const lists = run.data?.lists ?? [];
  const allItems = lists.flatMap((list) => list.items);
  const done = allItems.filter((item) => item.checkedAt).length;
  const error = run.error instanceof ApiError ? run.error.message : null;

  return (
    <div className="mx-auto flex max-w-[40rem] flex-col gap-5 px-5 py-6">
      <header>
        <Link
          to=".."
          relative="path"
          className={`text-[13px] ${LINK_CLASS}`}
        >
          Back to the session
        </Link>
        <h1 className="mt-2 font-display text-[28px] leading-tight">Run of show</h1>
        {allItems.length > 0 ? (
          <p className="tabular mt-1 text-[14px] text-ink-2">
            {done} of {allItems.length} done
          </p>
        ) : null}
      </header>

      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {failed ? <ErrorNote>{failed}</ErrorNote> : null}

      {lists.length === 0 ? (
        /*
          The platform ships before the commissioner has written a playbook. Saying so is better
          than an empty page that looks broken, and better than inventing a list.
        */
        <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] leading-relaxed text-ink-3">
          There is no run of show yet. HQ writes it, and it will appear here the next time you open
          a session.
        </p>
      ) : (
        lists.map((list) => (
          <section key={list.id}>
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
              {PHASE_LABEL[list.phase] ?? list.label}
            </h2>

            <ul className="mt-2 flex flex-col divide-y divide-light overflow-hidden rounded-md border border-light bg-paper">
              {list.items.map((item) => (
                <Row
                  key={item.id}
                  item={item}
                  sessionId={sessionId!}
                  onToggle={(checked) => tick.mutate({ itemId: item.id, checked })}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function Row({
  item,
  sessionId,
  onToggle,
}: {
  item: ChecklistItem;
  sessionId: string;
  onToggle: (checked: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const checked = item.checkedAt !== null;

  async function upload(file: File) {
    setUploading(true);
    setUploadError(null);

    try {
      const form = new FormData();
      form.append('photo', file);

      const response = await fetch(`/api/run-of-show/items/${item.id}/photo`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? 'That photo did not save.');
      }

      await queryClient.invalidateQueries({ queryKey: ['run-of-show', sessionId] });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'That photo did not save.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <li>
      {/*
        THE WHOLE ROW IS THE BUTTON. A checkbox is an 18px target for a thumb that is moving in a
        room that is filling up. 60px minimum, and the tick is drawn rather than an input.
      */}
      <button
        type="button"
        onClick={() => onToggle(!checked)}
        aria-pressed={checked}
        className="flex min-h-[60px] w-full items-center gap-3.5 px-4 py-3 text-left active:bg-sand"
      >
        <span
          aria-hidden="true"
          className={[
            'flex size-7 shrink-0 items-center justify-center rounded-full border-2',
            checked ? 'border-deep bg-deep' : 'border-stroke',
          ].join(' ')}
        >
          {checked ? (
            <svg viewBox="0 0 20 20" className="size-4 text-on-deep" fill="none">
              <path
                d="M5 10.5l3.5 3.5L15 7"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </span>

        <span
          className={[
            'flex-1 text-[15px] leading-snug',
            checked ? 'text-ink-2 line-through decoration-1' : '',
          ].join(' ')}
        >
          {item.label}
        </span>
      </button>

      {item.requiresPhoto ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-light bg-sand px-4 py-2.5">
          {/*
            `capture="environment"` opens the rear camera straight away on a phone. She is standing
            in the room she is being asked to photograph; a file picker would be the wrong door.
          */}
          {/*
            LABELLED EVEN THOUGH IT IS HIDDEN. The visible control is the button beside it, and this
            input is only ever opened by that button — but it is still a focusable form control with
            no accessible name, which is a CRITICAL violation and reads as an unnamed file field to
            anything not using the button.
          */}
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            aria-label={`Photo for: ${item.label}`}
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
              event.target.value = '';
            }}
          />
          <Button variant="secondary" disabled={uploading} onClick={() => fileInput.current?.click()}>
            {uploading ? 'Sending…' : item.hasPhoto ? 'Take another' : 'Take the photo'}
          </Button>

          {item.hasPhoto ? (
            <a
              href={`/api/run-of-show/items/${item.id}/photo`}
              target="_blank"
              rel="noreferrer"
              className={`text-[13px] ${LINK_CLASS}`}
            >
              See it
            </a>
          ) : (
            <span className="text-[12px] text-ink-3">Taking it ticks this off.</span>
          )}

          {uploadError ? <span className="text-[12px] text-rust">{uploadError}</span> : null}
        </div>
      ) : null}
    </li>
  );
}
